/**
 * React Query hooks for the admin-only "Fias-owned plugins" section of the
 * contributor dashboard.
 *
 * Endpoints (all `requireAdmin`):
 *   GET    /admin/plugins/platform-owned
 *   GET    /admin/plugins/:archeId/maintainers
 *   POST   /admin/plugins/:archeId/maintainers        grant `publisher`
 *   DELETE /admin/plugins/:archeId/maintainers/:username
 *
 * The maintainer grant is the load-bearing one: ADR-019 derives a workspace
 * asset-library link's validity from "is some active member of this workspace an
 * active `can_publish` collaborator on this arche?", so taking a `publisher` row
 * is what lets an admin link a Fias-owned plugin to a shared library. That is
 * also why every mutation invalidates the arche's workspace-links key — the
 * grant flips `GET /contributor/arches/:id/workspaces` from 403 to 200.
 */

import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

export interface PlatformOwnedPluginsFilters {
  limit?: number;
  offset?: number;
}

/**
 * A page of Fias-owned plugin arches. Pagination only — see the route's schema
 * for why there is no text search.
 *
 * `enabled` is supplied by the caller's admin gate, so a non-admin never issues
 * the request at all (the server is still the authorization boundary).
 * `keepPreviousData` stops the table blanking while paging.
 */
export function usePlatformOwnedPlugins(
  filters: PlatformOwnedPluginsFilters,
  options?: { enabled?: boolean },
) {
  const { limit = 25, offset = 0 } = filters;
  return useQuery({
    queryKey: queryKeys.adminPlatformPlugins.list({ limit, offset }),
    queryFn: () => api.listPlatformOwnedPlugins({ limit, offset }),
    enabled: options?.enabled ?? true,
    placeholderData: keepPreviousData,
  });
}

/**
 * The collaborator roster of one Fias-owned plugin. Fetched lazily — the UI only
 * needs it when confirming a self-revoke, to warn when the caller is the last
 * member who can publish (removing them invalidates every workspace link on the
 * plugin).
 */
export function usePluginMaintainers(archeId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.adminPlatformPlugins.maintainers(archeId),
    queryFn: () => api.listPluginMaintainers(archeId),
    enabled: (options?.enabled ?? true) && !!archeId,
  });
}

/**
 * Invalidates everything a maintainer change affects: the admin list (whose
 * `youAreMaintainer` flag and link validity both move), that plugin's roster,
 * and the arche-side workspace-links query on the contributor arche page.
 */
function useInvalidateMaintainerState(archeId: string) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.adminPlatformPlugins.all });
    void queryClient.invalidateQueries({
      queryKey: queryKeys.contributorArches.workspaces(archeId),
    });
  };
}

/** Grants `publisher` on a Fias-owned plugin to the named account. */
export function useAddPluginMaintainer(archeId: string) {
  const invalidate = useInvalidateMaintainerState(archeId);
  return useMutation({
    mutationFn: (username: string) => api.addPluginMaintainer(archeId, username),
    onSuccess: invalidate,
  });
}

/** Revokes a maintainer. Idempotent; the platform owner row is protected server-side. */
export function useRemovePluginMaintainer(archeId: string) {
  const invalidate = useInvalidateMaintainerState(archeId);
  return useMutation({
    mutationFn: (username: string) => api.removePluginMaintainer(archeId, username),
    onSuccess: invalidate,
  });
}
